/**
 * Lipstick Try-On — Before/After comparison slider.
 *
 * Stacks two layers inside the preview stage:
 *   - bottom: bare model image (no lipstick)
 *   - top:    SVG lip overlay (lipstick applied, mix-blend-mode: multiply)
 *
 * A draggable handle clips the TOP layer with `clip-path: inset(0 X% 0 0)`,
 * so dragging reveals more/less lipstick over the bare model.
 *
 * Exposed as `window.TryOnCompareSlider`. Instantiated lazily by assets/tryon.js
 * when the user taps the "Compare" button.
 *
 * Usage:
 *   const slider = new TryOnCompareSlider();
 *   slider.mount(stageEl, {
 *     getModelUrl: () => 'https://.../model-3.jpg',
 *     getLipsHtml: () => '<svg>...</svg>',     // pre-built SVG for the chosen color + model
 *     onSliderMove: () => track('move_slider_tryon')
 *   });
 *   // later:
 *   slider.unmount();
 */
(function () {
  'use strict';

  function TryOnCompareSlider() {
    this.stage = null;
    this.root = null;
    this.handle = null;
    this.topLayer = null;
    this.opts = null;
    this.dragging = false;
    this.moveBound = null;
    this.endBound = null;
    this.firstMoveFired = false;

    this._previousStageHtml = null;
  }

  TryOnCompareSlider.prototype.mount = function (stageEl, opts) {
    if (!stageEl) return;
    this.stage = stageEl;
    this.opts = opts || {};

    // Stash the current preview so we can restore it on unmount.
    this._previousStageHtml = stageEl.innerHTML;

    var modelUrl = this.opts.getModelUrl && this.opts.getModelUrl();
    var lipsHtml = this.opts.getLipsHtml && this.opts.getLipsHtml();

    var iconSvg = ''
      + '<svg class="icon" width="30" height="30" viewBox="0 0 47 47" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">'
      +   '<circle cx="23.5" cy="23.5" r="23.5" fill="white"/>'
      +   '<path d="M28.0002 17L34.0251 23.0248L28.0249 29.025" stroke="#1D1D1D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>'
      +   '<path d="M18.0249 17L12.0001 23.0248L18.0002 29.025" stroke="#1D1D1D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>'
      + '</svg>';

    stageEl.innerHTML = ''
      + '<div class="tryon-compare" data-tryon-compare-root>'
      +   '<div class="tryon-compare__handle" data-tryon-compare-handle role="slider" tabindex="0" aria-label="Drag to compare">'
      +     iconSvg
      +   '</div>'
      +   '<div class="tryon-compare__top" data-tryon-compare-top>'
      +     (lipsHtml || '')
      +   '</div>'
      +   '<div class="tryon-compare__bottom">'
      +     '<img src="' + (modelUrl || '') + '" alt="">'
      +   '</div>'
      + '</div>';

    this.root = stageEl.querySelector('[data-tryon-compare-root]');
    this.topLayer = stageEl.querySelector('[data-tryon-compare-top]');
    this.handle = stageEl.querySelector('[data-tryon-compare-handle]');
    this.firstMoveFired = false;

    var self = this;
    this.moveBound = function (e) { self._onMove(e); };
    this.endBound = function () { self._onEnd(); };
    this._onStart = function (e) { self._beginDrag(e); };

    this.handle.addEventListener('mousedown', this._onStart);
    this.handle.addEventListener('touchstart', this._onStart, { passive: true });
    this.handle.addEventListener('keydown', this._onKeyDown.bind(this));

    // Initial position: centre
    requestAnimationFrame(function () { self._setPercent(50); });
  };

  /**
   * Re-paint the slider's bottom image and top lip SVG without unmounting.
   * Called from tryon.js when state.color or state.modelUrl change while
   * compare mode is active, so the user can switch shades/models without
   * having to exit compare first (matches the reactive behaviour of the
   * Next.js <ComparisonSlider choosedColor={...} modal={...}> component).
   *
   * Preserves the current handle position / clip-path so the user's drag
   * isn't reset on every shade tap.
   */
  TryOnCompareSlider.prototype.refresh = function () {
    if (!this.stage || !this.opts || !this.root) return;

    var newModelUrl = this.opts.getModelUrl && this.opts.getModelUrl();
    var newLipsHtml = this.opts.getLipsHtml && this.opts.getLipsHtml();

    var bottomImg = this.root.querySelector('.tryon-compare__bottom img');
    if (bottomImg && newModelUrl && bottomImg.getAttribute('src') !== newModelUrl) {
      bottomImg.setAttribute('src', newModelUrl);
    }
    if (this.topLayer) {
      this.topLayer.innerHTML = newLipsHtml || '';
    }
  };

  TryOnCompareSlider.prototype.unmount = function () {
    if (!this.stage) return;
    this._removeWindowListeners();
    if (this.handle && this._onStart) {
      this.handle.removeEventListener('mousedown', this._onStart);
      this.handle.removeEventListener('touchstart', this._onStart);
    }
    if (this._previousStageHtml != null) {
      this.stage.innerHTML = this._previousStageHtml;
    }
    this.stage = null;
    this.root = null;
    this.handle = null;
    this.topLayer = null;
    this.opts = null;
    this._previousStageHtml = null;
  };

  TryOnCompareSlider.prototype._beginDrag = function (e) {
    this.dragging = true;
    this.firstMoveFired = false;
    window.addEventListener('mousemove', this.moveBound);
    window.addEventListener('touchmove', this.moveBound, { passive: true });
    window.addEventListener('mouseup', this.endBound);
    window.addEventListener('touchend', this.endBound);
  };

  TryOnCompareSlider.prototype._onMove = function (e) {
    if (!this.dragging || !this.root) return;
    var clientX = (e.touches && e.touches[0]) ? e.touches[0].clientX : e.clientX;
    if (clientX == null) return;
    var rect = this.root.getBoundingClientRect();
    var pct = ((clientX - rect.left) / rect.width) * 100;
    this._setPercent(pct);
    if (!this.firstMoveFired) {
      this.firstMoveFired = true;
      if (this.opts && typeof this.opts.onSliderMove === 'function') {
        try { this.opts.onSliderMove(); } catch (e2) {}
      }
    }
  };

  TryOnCompareSlider.prototype._onEnd = function () {
    this.dragging = false;
    this._removeWindowListeners();
  };

  TryOnCompareSlider.prototype._removeWindowListeners = function () {
    if (!this.moveBound) return;
    window.removeEventListener('mousemove', this.moveBound);
    window.removeEventListener('touchmove', this.moveBound);
    window.removeEventListener('mouseup', this.endBound);
    window.removeEventListener('touchend', this.endBound);
  };

  TryOnCompareSlider.prototype._onKeyDown = function (e) {
    var step = 5;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this._setPercent(this._currentPercent() - step);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this._setPercent(this._currentPercent() + step);
    }
  };

  TryOnCompareSlider.prototype._currentPercent = function () {
    if (!this.handle || !this.root) return 50;
    var rect = this.root.getBoundingClientRect();
    var leftPx = parseFloat(this.handle.style.left) || (rect.width / 2);
    if (this.handle.style.left && this.handle.style.left.indexOf('%') !== -1) {
      return parseFloat(this.handle.style.left);
    }
    return (leftPx / rect.width) * 100;
  };

  TryOnCompareSlider.prototype._setPercent = function (pct) {
    if (pct < 2) pct = 2;
    if (pct > 98) pct = 98;
    this.handle.style.left = pct + '%';
    this.topLayer.style.clipPath = 'inset(0 ' + (100 - pct) + '% 0 0)';
    this.topLayer.style.webkitClipPath = 'inset(0 ' + (100 - pct) + '% 0 0)';
  };

  window.TryOnCompareSlider = TryOnCompareSlider;
})();
